/**
 * Student Dashboard Constants
 * Pure static data organized by currentStep values from backend
 * No functions - only data (functions moved to stepHelpers.js)
 * 
 * This module contains the consolidated STEP_DEFINITIONS structure that replaces:
 * - STEP_CONTENT (removed)
 * - STEP_ACTIONS (removed) 
 * - STEP_CONFIGURATIONS (removed)
 * - APPLICATION_TYPES concept (removed)
 * - subType concept (removed)
 * 
 * All step data is now accessed via STEP_DEFINITIONS[currentStep] pattern.
 */
import { EXTERNAL_ACTION_TYPES } from '../constants/externalConfig';

/**
 * Step status constants
 */
export const STEP_STATUS = {
  COMPLETE: 'COMPLETE',
  IN_PROGRESS: 'IN_PROGRESS',
  PENDING: 'PENDING',
  INPROGRESS: 'INPROGRESS',  // API status value
  EXTERNAL_COMPLETE: 'EXTERNAL_COMPLETE'  // Completed in our system, handed off to external system
};

/**
 * Steps & Values used in stepDefinitions. Defines the timeline & content used.
 */
export const CURRENT_STEP_VALUES = {
  HONORS_INPROGRESS: "HONORS_INPROGRESS",
  HONORS_OFFER: "HONORS_OFFER",
  HONORS_DENY: "HONORS_DENY",
  HONORS_DECLINED: "HONORS_DECLINED",
  HONORS_PENDING_DECLINE: "HONORS_PENDING_DECLINE",
  HONORS_DECISION_PENDING: "HONORS_DECISION_PENDING",
  HONORS_WITHDRAWN: "HONORS_WITHDRAWN",
  SCHOLARS_PREFERENCES: "SCHOLARS_PREFERENCES",
  SCHOLARS_PREFERENCES_SUBMITTED: "SCHOLARS_PREFERENCES_SUBMITTED",

  HONORS_COMPLETE: "HONORS_COMPLETE",

  // Scholars-only (native scholars — no honors application)
  SCHOLARS_AWAITING: "SCHOLARS_AWAITING",
  SCHOLARS_INPROGRESS: "SCHOLARS_INPROGRESS",
  SCHOLARS_NATIVE_PREFERENCES: "SCHOLARS_NATIVE_PREFERENCES",
  SCHOLARS_NATIVE_SUBMITTED: "SCHOLARS_NATIVE_SUBMITTED",

  // Scholars published decision (set once the outcome is published to PeopleSoft).
  SCHOLARS_ADMITTED: "SCHOLARS_ADMITTED",
  SCHOLARS_DENIED: "SCHOLARS_DENIED",
  SCHOLARS_DECLINED: "SCHOLARS_DECLINED",
  SCHOLARS_WITHDRAWN: "SCHOLARS_WITHDRAWN",
}

/**
 * Consolidated step definitions organized by currentStep values from backend
 * Each currentStep contains all related content, actions, templateType, and additionalResources
 */
export const STEP_DEFINITIONS = {
  // HONORS_INPROGRESS - Application under review
  HONORS_INPROGRESS: {
    content: {
      title: 'Application Received and Under Review',
      description: 'Your application is being processed and reviewed by our admissions team.',
      message: 'We have received your Honors application and it is currently under review. Our admissions team is carefully evaluating all submitted materials.',
      additionalInfo: 'The review process typically takes 4-6 weeks. You will be notified via email once a decision has been made.',
      contactInfo: '{generalQuestions}'
    },
    actions: [],
    templateType: 'under-review',
    additionalResources: {
      programInfo: {
        title: 'Program Information',
        description: 'Learn more about the Honors program requirements and benefits.',
        buttonText: 'Learn More'
      },
      academicSupport: {
        title: 'Academic Support',
        description: 'Explore academic resources and support services available to you.',
        buttonText: 'View Resources'
      }
    }
  },

  // HONORS_OFFER - Student accepted to Honors
  HONORS_OFFER: {
    content: {
      title: 'Decision',
      congratsMessage: 'Congratulations! You\'ve been accepted to University Honors',
      description: 'Your place in the University Honors Program is secured once you confirm enrollment by paying the $100 university acceptance fee by {deadline}.',
      message: 'Use the accept/decline option in your Applicant Center to confirm your enrollment.',
      additionalInfo: '{questionsContactAdmissions}',
      contactInfo: '{officeContact}',
      severity: 'success'
    },
    actions: [
      {
        type: 'decline-honors',
        label: 'Decline Offer',
        variant: 'outlined',
        color: 'error'
      },
      {
        type: 'contact',
        label: 'Contact Office',
        variant: 'outlined',
        color: 'primary'
      },
      {
        type: 'accept',
        label: 'Accept on Applicant Center',
        href: 'https://appstatus.osu.edu/',
        variant: 'outlined',
        color: 'primary'
      }
    ],
    templateType: 'decision',
    declineModal: {
      title: 'Decline Honors Offer',
      message: 'Are you sure you want to decline your University Honors Program offer? This action cannot be undone.',
      confirmButtonText: 'Yes, Decline Offer',
      cancelButtonText: 'Cancel'
    },
    additionalResources: {
      programInfo: {
        title: 'Explore the Honors Communities',
        description: 'Join one of our three Honors residential communities to enhance your Honors experience.',
        buttonText: 'Learn more',
        href: 'https://honors-scholars.osu.edu/honors/community '
      },
      academicSupport: {
        title: 'Prepare for Honors Orientation',
        description: 'Register for a special Honors orientation to get started on your Honors journey',
        buttonText: 'Explore Orientation Dates',
        href: 'https://orientation.osu.edu/autumn/schedule.html'
      }
    }
  },

  // HONORS_DENY - Student denied from Honors
  HONORS_DENY: {
    content: {
      title: 'Honors Scholars Deny',
      mainMessage: 'Thank you for your application to Honors and Scholars',
      description: 'We\'ve carefully considered your application and, while we\'re unable to offer you admission to the Honors or Scholars Programs as an incoming freshman, we appreciate your interest in our academic community.',
      message: 'You can apply for University Honors after your first semester if you achieve a 3.4-3.5 GPA in your college.',
      additionalInfo: null,
      contactInfo: '{questionsContact}',
      severity: 'info'
    },
    actions: [],
    templateType: 'decision',
    additionalResources: {
      programInfo: {
        title: 'Future Opportunities',
        description: 'Learn about applying to Honors after your first semester with a 3.4+ GPA.',
        buttonText: 'Learn More'
      },
      academicSupport: {
        title: 'Academic Support',
        description: 'Explore academic resources and support services available to you.',
        buttonText: 'View Resources'
      }
    }
  },

  // SCHOLARS_PREFERENCES - Student invited to Scholars, needs to submit preferences
  SCHOLARS_PREFERENCES: {
    content: {
      title: 'Honors to Scholars Preferences',
      description: 'Unfortunately, we are not able to offer you a place in the Honors Program. However, we would like to invite you to advance in the Ohio State Scholars Program process.',
      message: 'Scholars offers 16 themed living-learning communities and the opportunity to join Honors after your first semester.',
      additionalInfo: 'Click below to explore programs and submit your preferences.',
      contactInfo: '{questionsContact}'
    },
    actions: [
      {
        type: EXTERNAL_ACTION_TYPES.ACCEPT_INVITATION,
        label: 'Begin Preferencing',
        variant: 'outlined',
        color: 'inherit',
        sx: (theme) => ({
          color: theme.palette.colors.purple,
          borderColor: theme.palette.colors.purple,
        })
      },
      {
        type: EXTERNAL_ACTION_TYPES.EXPLORE_SCHOLARS,
        label: 'Explore Scholars Programs',
        variant: 'contained',
        sx: (theme) => ({
          backgroundColor: theme.palette.colors.purple,
        })
      }
    ],
    templateType: 'invitation',
    additionalResources: {
      programInfo: {
        title: 'Program Information',
        description: 'Learn more about the Scholars program requirements and benefits to help with your decision.',
        buttonText: 'Learn More'
      },
      academicSupport: {
        title: 'Academic Support',
        description: 'Explore academic resources and support services available to you.',
        buttonText: 'View Resources'
      }
    }
  },

  // SCHOLARS_PREFERENCES_SUBMITTED - Preferences submitted, under review
  SCHOLARS_PREFERENCES_SUBMITTED: {
    content: {
      showScholarsDashLink: true,
      title: 'Scholars Application Under Review',
      description: 'Your application is now in the Scholars portal.',
      message: 'Thank you for completing your Scholars program preferences. Your application has been submitted to the Scholars admissions team.',
      additionalInfo: 'Please wait for further communication via email regarding your Scholars program decision. You will be notified once a decision has been made.',
      contactInfo: '{questionsContact}',
    },
    actions: [],
    templateType: 'under-review',
    additionalResources: {
      programInfo: {
        title: 'Program Information',
        description: 'Learn more about the Scholars program requirements and benefits.',
        buttonText: 'Learn More'
      },
      academicSupport: {
        title: 'Academic Support',
        description: 'Explore academic resources and support services available to you.',
        buttonText: 'View Resources'
      }
    }
  },

  // HONORS_DECLINED - Student declined their Honors offer
  HONORS_DECLINED: {
    content: {
      title: 'Applicant Action Completed',
      description: 'You have declined your offer to the University Honors Program.',
      message: 'Thank you for considering the University Honors Program. We wish you success in your academic journey.',
      contactInfo: '{questionsContact}'
    },
    actions: [],
    templateType: 'decision',
    additionalResources: {
      programInfo: {
        title: 'Program Information',
        description: 'Learn more about the program requirements and benefits.',
        buttonText: 'Learn More'
      },
      academicSupport: {
        title: 'Academic Support',
        description: 'Explore academic resources and support services available to you.',
        buttonText: 'View Resources'
      }
    }
  },
  // HONORS_PENDING_DECLINE - Student submitted the decline to their Honors offer
  HONORS_PENDING_DECLINE: {
    content: {
      title: 'Application Decision',
      description: 'You have declined your offer to the University Honors Program.',
      message: 'Thank you for considering the University Honors Program. We wish you success in your academic journey.',
      contactInfo: '{questionsContact}'
    },
    actions: [],
    templateType: 'decision',
    additionalResources: {
      programInfo: {
        title: 'Program Information',
        description: 'Learn more about the program requirements and benefits.',
        buttonText: 'Learn More'
      },
      academicSupport: {
        title: 'Academic Support',
        description: 'Explore academic resources and support services available to you.',
        buttonText: 'View Resources'
      }
    }
  },

  // HONORS_WITHDRAWN - Application withdrawn
  HONORS_WITHDRAWN: {
    content: {
      title: 'Applicant Action Completed',
      description: 'Your application has been withdrawn from review for the University Honors Program and the Ohio State Scholars program.',
      // message: 'Your application to the University Honors Program has been withdrawn.',
      // additionalInfo: '{statusQuestions}',
      // contactInfo: 'Contact us at {honorsScholarsContact}'
      contactInfo: '{statusQuestions}'
    },
    actions: [],
    templateType: 'decision',
    additionalResources: {
      programInfo: {
        title: 'Program Information',
        description: 'Learn more about the program requirements and benefits.',
        buttonText: 'Learn More'
      },
      academicSupport: {
        title: 'Academic Support',
        description: 'Explore academic resources and support services available to you.',
        buttonText: 'View Resources'
      }
    }
  },

  // HONORS_DECISION_PENDING - Pending decision step
  HONORS_DECISION_PENDING: {
    content: {
      title: 'Decision',
      description: 'You will receive a decision once your application review is complete.',
      message: 'Your application is currently under review and you will be notified once a decision has been made.',
      contactInfo: '{questionsContact}'
    },
    actions: [],
    templateType: 'under-review',
    additionalResources: {
      programInfo: {
        title: 'Program Information',
        description: 'Learn more about the program requirements and benefits.',
        buttonText: 'Learn More'
      },
      academicSupport: {
        title: 'Academic Support',
        description: 'Explore academic resources and support services available to you.',
        buttonText: 'View Resources'
      }
    }
  },


  // HONORS_COMPLETE - Completed honors application
  HONORS_COMPLETE: {
    content: {
      title: 'Honors Application Complete',
      description: 'Your Honors application was processed.',
      message: 'Your University Honors application has been successfully completed.',
      contactInfo: '{questionsContact}'
    },
    actions: [],
    templateType: 'under-review',
    additionalResources: {
      programInfo: {
        title: 'Program Information',
        description: 'Learn more about the program requirements and benefits.',
        buttonText: 'Learn More'
      },
      academicSupport: {
        title: 'Academic Support',
        description: 'Explore academic resources and support services available to you.',
        buttonText: 'View Resources'
      }
    }
  },

  // // SCHOLARS_INVITATION_ACCEPTED - Accepted scholars invitation
  // SCHOLARS_INVITATION_ACCEPTED: {
  //   content: {
  //     title: 'Scholars Program Invitation',
  //     description: 'You accepted the invitation and submitted your preferences.',
  //     message: 'Thank you for accepting the Scholars Program invitation and submitting your preferences.',
  //     contactInfo: 'Questions? Contact us at honors-scholars@osu.edu or (614) 292-3135'
  //   },
  //   actions: [],
  //   templateType: 'invitation',
  //   additionalResources: {
  //     programInfo: {
  //       title: 'Program Information',
  //       description: 'Learn more about the Scholars program requirements and benefits.',
  //       buttonText: 'Learn More'
  //     },
  //     academicSupport: {
  //       title: 'Academic Support',
  //       description: 'Explore academic resources and support services available to you.',
  //       buttonText: 'View Resources'
  //     }
  //   }
  // }

  // =========================================================================
  // SCHOLARS-ONLY (native scholars — no honors application)
  // =========================================================================

  // SCHOLARS_AWAITING - Admin has not yet triggered preferencing for this student
  SCHOLARS_AWAITING: {
    content: {
      title: 'Scholars Application Received',
      description: 'Your Scholars application has been received and is being processed.',
      additionalInfo: 'No action is needed from you at this time.',
      contactInfo: '{questionsContact}'
    },
    actions: [],
    templateType: 'under-review',
    additionalResources: {
      programInfo: {
        title: 'Explore Scholars Programs',
        description: 'Learn more about the 16 Scholars living-learning communities.',
        buttonText: 'Learn More',
        href: 'https://honors-scholars.osu.edu/scholars/programs'
      },
      academicSupport: {
        title: 'Academic Support',
        description: 'Explore academic resources and support services available to you.',
        buttonText: 'View Resources'
      }
    }
  },

  // SCHOLARS_INPROGRESS - Catch-all for scholars-only applicants in non-preferencing statuses
  SCHOLARS_INPROGRESS: {
    content: {
      title: 'Scholars Application In Progress',
      description: 'Your Scholars application is being reviewed.',
      message: 'Your application to the Scholars Program is in progress. You will be notified via email when there is an update or action needed.',
      contactInfo: '{questionsContact}'
    },
    actions: [],
    templateType: 'under-review',
    additionalResources: {
      programInfo: {
        title: 'Explore Scholars Programs',
        description: 'Learn more about the 16 Scholars living-learning communities.',
        buttonText: 'Learn More',
        href: 'https://honors-scholars.osu.edu/scholars/programs'
      },
      academicSupport: {
        title: 'Academic Support',
        description: 'Explore academic resources and support services available to you.',
        buttonText: 'View Resources'
      }
    }
  },

  // SCHOLARS_NATIVE_PREFERENCES - Native scholars student ready to submit preferences
  // (differs from SCHOLARS_PREFERENCES which is for honors→scholars students)
  SCHOLARS_NATIVE_PREFERENCES: {
    content: {
      title: 'Select Your Scholars Program Preferences',
      description: 'You have been invited to select your Scholars program preferences.',
      message: 'Scholars offers 16 themed living-learning communities. Select Explore Scholars Programs to learn about each program\u2019s unique experiential and residential opportunities.',
      additionalInfo: 'Select Begin Preferencing to review the available programs, rank your top choices, and submit your preferences to proceed to Scholars application review.',
      contactInfo: '{questionsContact}'
    },
    actions: [
      {
        type: EXTERNAL_ACTION_TYPES.ACCEPT_INVITATION,
        label: 'Begin Preferencing',
        variant: 'outlined',
        color: 'inherit',
        sx: (theme) => ({
          color: theme.palette.colors.purple,
          borderColor: theme.palette.colors.purple,
        })
      },
      {
        type: EXTERNAL_ACTION_TYPES.EXPLORE_SCHOLARS,
        label: 'Explore Scholars Programs',
        variant: 'contained',
        sx: (theme) => ({
          backgroundColor: theme.palette.colors.purple,
        })
      }
    ],
    templateType: 'invitation',
    additionalResources: {
      programInfo: {
        title: 'Explore Scholars Programs',
        description: 'Learn more about the 16 Scholars living-learning communities.',
        buttonText: 'Learn More',
        href: 'https://honors-scholars.osu.edu/scholars/programs'
      },
      academicSupport: {
        title: 'Academic Support',
        description: 'Explore academic resources and support services available to you.',
        buttonText: 'View Resources'
      }
    }
  },

  // SCHOLARS_NATIVE_SUBMITTED - Native scholars student has submitted preferences
  SCHOLARS_NATIVE_SUBMITTED: {
    content: {
      showScholarsDashLink: true,
      title: 'Scholars Native Submitted',
      description: 'Your Scholars program preferences have been submitted and are under review.',
      message: 'Thank you for submitting your Scholars program preferences. Your application is now being reviewed by the Scholars admissions team.',
      additionalInfo: 'Please wait for further communication via email regarding your Scholars program decision.',
      contactInfo: '{questionsContact}'
    },
    actions: [],
    templateType: 'under-review',
    additionalResources: {
      programInfo: {
        title: 'Explore Scholars Programs',
        description: 'Learn more about the 16 Scholars living-learning communities.',
        buttonText: 'Learn More',
        href: 'https://honors-scholars.osu.edu/scholars/programs'
      },
      academicSupport: {
        title: 'Academic Support',
        description: 'Explore academic resources and support services available to you.',
        buttonText: 'View Resources'
      }
    }
  },

  // SCHOLARS_ADMITTED - Scholars admit decision published to PeopleSoft (mirrors HONORS_OFFER).
  // has607: cloned from HONORS_OFFER with the admitted sub-program name ({scholarsProgram}) in place
  // of "University Honors", a fixed "May 1" acceptance-fee date, and no "Accept on Applicant Center"
  // button.
  SCHOLARS_ADMITTED: {
    content: {
      title: 'Decision',
      congratsMessage: 'Congratulations! You\'ve been accepted to {scholarsProgram}',
      description: 'Your place in {scholarsProgram} is secured once you confirm enrollment by paying the $100 university acceptance fee by May 1.',
      message: 'Use the accept/decline option in your Applicant Center to confirm your enrollment.',
      additionalInfo: '{questionsContactAdmissions}',
      contactInfo: '{officeContact}',
      severity: 'success'
    },
    actions: [
      {
        type: 'decline-scholars',
        label: 'Decline Offer',
        variant: 'outlined',
        color: 'error'
      },
      {
        type: 'contact',
        label: 'Contact Office',
        variant: 'outlined',
        color: 'primary'
      }
    ],
    templateType: 'decision',
    declineModal: {
      title: 'Decline Scholars Offer',
      message: 'Are you sure you want to decline your Scholars Program offer? This action cannot be undone.',
      confirmButtonText: 'Yes, Decline Offer',
      cancelButtonText: 'Cancel'
    },
    additionalResources: {
      programInfo: {
        title: 'Explore Scholars Programs',
        description: 'Learn more about the 16 Scholars living-learning communities.',
        buttonText: 'Learn More',
        href: 'https://honors-scholars.osu.edu/scholars/programs'
      },
      academicSupport: {
        title: 'Prepare for Orientation',
        description: 'Register for orientation to get started on your Scholars journey.',
        buttonText: 'Explore Orientation Dates',
        href: 'https://orientation.osu.edu/autumn/schedule.html'
      }
    }
  },

  // SCHOLARS_DENIED - Scholars deny decision published to PeopleSoft (mirrors HONORS_DENY)
  SCHOLARS_DENIED: {
    content: {
      title: 'Decision',
      mainMessage: 'Thank you for your application to the Scholars Program',
      description: 'We\'ve carefully considered your application and, while we\'re unable to offer you a place in the Scholars Program at this time, we appreciate your interest in our academic community.',
      additionalInfo: null,
      contactInfo: '{questionsContact}',
      severity: 'info'
    },
    actions: [],
    templateType: 'decision',
    additionalResources: {
      programInfo: {
        title: 'Future Opportunities',
        description: 'Learn about applying to Honors after your first semester with a qualifying GPA.',
        buttonText: 'Learn More'
      },
      academicSupport: {
        title: 'Academic Support',
        description: 'Explore academic resources and support services available to you.',
        buttonText: 'View Resources'
      }
    }
  },

  // SCHOLARS_DECLINED - Student declined their published Scholars admit (mirrors HONORS_DECLINED)
  SCHOLARS_DECLINED: {
    content: {
      title: 'Applicant Action Completed',
      description: 'You have declined your offer to the Ohio State Scholars Program.',
      message: 'Thank you for considering the Ohio State Scholars Program. We wish you success in your academic journey.',
      contactInfo: '{questionsContact}'
    },
    actions: [],
    templateType: 'decision',
    additionalResources: {
      programInfo: {
        title: 'Program Information',
        description: 'Learn more about the program requirements and benefits.',
        buttonText: 'Learn More'
      },
      academicSupport: {
        title: 'Academic Support',
        description: 'Explore academic resources and support services available to you.',
        buttonText: 'View Resources'
      }
    }
  },

  // SCHOLARS_WITHDRAWN - Application withdrawn (mirrors HONORS_WITHDRAWN)
  SCHOLARS_WITHDRAWN: {
    content: {
      title: 'Applicant Action Completed',
      description: 'Your application has been withdrawn from review for the Ohio State Scholars Program.',
      contactInfo: '{statusQuestions}'
    },
    actions: [],
    templateType: 'decision',
    additionalResources: {
      programInfo: {
        title: 'Program Information',
        description: 'Learn more about the program requirements and benefits.',
        buttonText: 'Learn More'
      },
      academicSupport: {
        title: 'Academic Support',
        description: 'Explore academic resources and support services available to you.',
        buttonText: 'View Resources'
      }
    }
  },
};

// Clean exports - no functions, just pure data
const constants = {
  STEP_STATUS,
  STEP_DEFINITIONS
};

export default constants;